import React, { useState, useEffect } from 'react';
import { HospitalGroup } from '../types';
import { X, Building2, Save, ImageIcon, LinkIcon, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import * as storageService from '../services/storageService';

interface GroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    group?: HospitalGroup | null; // If null, creating new
    onSave: () => void;
}

const GroupModal: React.FC<GroupModalProps> = ({ isOpen, onClose, group, onSave }) => {
    const [name, setName] = useState('');
    const [handle, setHandle] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (group) {
            setName(group.name);
            setHandle(group.handle);
            setLogoUrl(group.logoUrl || '');
            setIsActive(group.isActive);
        } else {
            setName('');
            setHandle('');
            setLogoUrl('');
            setIsActive(true);
        }
    }, [group, isOpen]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setLogoUrl(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        if (!name.trim()) return alert('El nombre es obligatorio');
        
        setIsSaving(true);
        try {
            const groupData = {
                id: group?.id,
                name,
                handle,
                logoUrl,
                isActive
            };

            await storageService.saveHospitalGroup(groupData, !group);
            onSave();
            onClose();
        } catch (error) {
            console.error('Error saving group', error);
            alert('Error al guardar la Cadena Hospitalaria.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-fade-in">
                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform"></div>
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-3 relative z-10">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${group ? 'bg-indigo-100 text-indigo-600' : 'bg-emerald-100 text-emerald-600'} shadow-sm`}>
                            <Building2 className="w-5 h-5" />
                        </div>
                        {group ? `Editar Cadena: ${group.name}` : 'Crear Nueva Cadena Hospitalaria'}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors relative z-10">
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-8 bg-white space-y-6">
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Nombre Comercial de la Cadena</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => {
                                const newName = e.target.value;
                                setName(newName);
                                if (!group) {
                                    setHandle(newName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
                                }
                            }}
                            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-bold text-slate-700 transition-all text-lg"
                            placeholder="Ej. Grupo Christus Muguerza"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"><LinkIcon className="w-4 h-4" /> Identificador (Handle)</label>
                            <div className="flex items-center gap-1 font-mono text-sm bg-slate-50 p-3 rounded-xl border border-slate-200 focus-within:ring-2 focus-within:ring-indigo-500 transition-all">
                                <span className="text-slate-400 font-bold">@</span>
                                <input
                                    type="text"
                                    value={handle}
                                    onChange={e => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                    className="bg-transparent border-0 focus:ring-0 p-0 font-bold w-full outline-none text-slate-700"
                                    placeholder="grupo-muguerza"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Estado Principal</label>
                            <button
                                onClick={() => setIsActive(!isActive)}
                                className={`w-full p-3.5 rounded-xl flex items-center justify-between font-bold border transition-all ${isActive ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
                            >
                                <span>{isActive ? 'Activa / Operativa' : 'Suspendida'}</span>
                                {isActive ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Logo Corporativo / Estandarte</label>
                        <div className="flex items-center gap-6 p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                            <div className="w-20 h-20 bg-white rounded-xl shadow-sm border border-slate-100 flex items-center justify-center p-2 overflow-hidden transition-all hover:scale-105">
                                {logoUrl ? <img src={logoUrl} className="w-full h-full object-contain" /> : <Building2 className="w-8 h-8 text-slate-300" />}
                            </div>
                            <div className="flex-1">
                                <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" id="group-logo-up" />
                                <label htmlFor="group-logo-up" className="inline-block px-5 py-2.5 bg-indigo-50 text-indigo-600 rounded-lg text-sm font-bold cursor-pointer hover:bg-indigo-100 hover:shadow-sm transition-all shadow-indigo-500/10">Subir Emblema</label>
                                <input type="text" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="O pega una URL directa de la imagen..." className="block w-full mt-3 text-xs p-3 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3 rounded-b-3xl">
                    <button onClick={onClose} className="px-5 py-2.5 text-slate-500 font-bold hover:bg-slate-200 rounded-xl transition-colors">
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-indigo-600/30 transition-all active:scale-95 disabled:opacity-70 disabled:pointer-events-none"
                    >
                        <Save className="w-5 h-5" />
                        {isSaving ? 'Guardando...' : 'Guardar Cadena'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GroupModal;
